import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get } from '@ember/object';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import sortBy from '@nullvoxpopuli/ember-composable-helpers/helpers/sort-by';
import can from 'ember-can/helpers/can';
import { task, type Task } from 'ember-concurrency';
import { not, or } from 'ember-truth-helpers';
import type Flow from 'garaje/models/flow';
import type Location from 'garaje/models/location';
import type SignInField from 'garaje/models/sign-in-field';
import { filter } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

const HIDDEN_FIELD_IDENTIFIERS = Object.freeze(['host', 'name']);

interface SignInFieldsReturningVisitorsSignature {
  Args: {
    signInFields: SignInField[];
    flow: Flow;
    config?: Location['config'];
    saveFieldsTask: Task<void, []>;
    isDisabled?: boolean;
    isPristine: boolean;
    onChange?: (field: SignInField, attr: string) => void;
  };
}

const configurableFieldsFilterFn = (field: SignInField): boolean => {
  return !HIDDEN_FIELD_IDENTIFIERS.includes(field.identifier);
};

export default class SignInFieldsReturningVisitors extends Component<SignInFieldsReturningVisitorsSignature> {
  @localCopy('args.signInFields') signInFields!: SignInField[];

  optionsName = 'returning-visitors';

  get isConfigured(): boolean {
    const { config, flow } = this.args;

    if (!config && flow.isGlobal) return true;

    // eslint-disable-next-line ember/no-get
    return Boolean(get(config, 'beenHereBefore'));
  }

  get isCTAVisible(): boolean {
    if (this.args.isDisabled) return false;

    return this.isNotEmpty;
  }

  get isCTADisabled(): boolean {
    return this.args.isDisabled || this.args.isPristine || this.saveReturningVisitorsOptionsTask.isRunning;
  }

  get isNotEmpty(): boolean {
    return this.configurableFields.length > 0;
  }

  @filter('signInFields', configurableFieldsFilterFn)
  configurableFields!: SignInField[];

  onChange(field: SignInField, attr: string): void {
    this.args.onChange?.(field, attr);
  }

  @action
  doAutoPopulatePreviousResponse(field: SignInField): void {
    if (!field.autoPopulatePreviousResponse) {
      this.onChange(field, 'autoPopulatePreviousResponse');
    }

    field.autoPopulatePreviousResponse = true;
  }

  @action
  doNotAutoPopulatePreviousResponse(field: SignInField): void {
    if (field.autoPopulatePreviousResponse) {
      this.onChange(field, 'autoPopulatePreviousResponse');
    }

    field.autoPopulatePreviousResponse = false;
  }

  saveReturningVisitorsOptionsTask = task(async (): Promise<void> => {
    await this.args.saveFieldsTask.perform();
  });

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Require returning visitors to respond to certain fields for every visit. Learn more about managing fields for
        <a href="https://envoy.help/en/articles/5670344-returning-visitors" target="_blank" rel="noopener">
          returning visitors →
        </a>
      </p>
      {{#if this.isConfigured}}
        {{#if this.isNotEmpty}}
          <div class="mb-8">
            <div data-test-field-list class="mt-6">
              <div class="flex text-carbon-50 font-bold">
                <span class="flex text-sm" style="flex:3">Field</span>
                <span id="returningVisitorsMostRecentHeading" class="flex-1 flex text-sm justify-center">
                  Most recent visit only
                </span>
                <span id="returningVisitorsEveryHeading" class="flex-1 flex text-sm justify-center">Every visit</span>
              </div>

              <hr class="my-2" />

              <div class="overflow-y-auto overscroll-y-contain" style="max-height:40vh;">
                {{#each (sortBy "position" this.configurableFields) as |field|}}
                  <div
                    data-test-configurable-field={{field.id}}
                    class="flex"
                    role="radiogroup"
                    aria-labelledby="returningVisitorsFieldName-{{field.id}}"
                  >
                    <p
                      id="returningVisitorsFieldName-{{field.id}}"
                      class="flex text-sm text-carbon-50 m-0"
                      style="flex:3"
                    >
                      {{field.name}}
                    </p>

                    <div class="flex-1 flex items-center">
                      <input
                        data-test-autopopulate-field-y
                        class="my-0 mx-auto"
                        type="radio"
                        name="returning-visitors-{{field.id}}"
                        disabled={{or @isDisabled field.isAutoPopulatePreviousResponseLocked}}
                        checked={{field.autoPopulatePreviousResponse}}
                        aria-labelledby="returningVisitorsMostRecentHeading"
                        {{on "click" (fn this.doAutoPopulatePreviousResponse field)}}
                      />
                    </div>

                    <div class="flex-1 flex items-center">
                      <input
                        data-test-autopopulate-field-n
                        class="my-0 mx-auto"
                        type="radio"
                        name="returning-visitors-{{field.id}}"
                        disabled={{or @isDisabled field.isAutoPopulatePreviousResponseLocked}}
                        checked={{not field.autoPopulatePreviousResponse}}
                        aria-labelledby="returningVisitorsEveryHeading"
                        {{on "click" (fn this.doNotAutoPopulatePreviousResponse field)}}
                      />
                    </div>
                  </div>

                  <hr class="my-2" />
                {{/each}}
              </div>
            </div>
          </div>
        {{else}}
          <p data-test-empty-text class="text-center text-carbon-40 mt-12 mb-24">
            You have no fields to manage.
          </p>
        {{/if}}

        {{#if this.isCTAVisible}}
          <button
            disabled={{this.isCTADisabled}}
            class="btn btn--small btn--brand float-right mb-8
              {{if this.saveReturningVisitorsOptionsTask.isRunning 'disabled'}}
              "
            {{on "click" this.saveReturningVisitorsOptionsTask.perform}}
            data-test-save-options={{this.optionsName}}
          >
            {{#if this.saveReturningVisitorsOptionsTask.isRunning}}
              Saving...
            {{else}}
              Save
            {{/if}}
          </button>
        {{/if}}
      {{else}}
        <p data-test-not-configured class="text-center text-carbon-40 pt-8 pb-16">
          Returning visitors is currently disabled.
          {{#if (can "visit advance-settings")}}
            <br />
            To enable this feature, visit
            <LinkTo @route="visitors.settings.welcome-screen.returning-visitors">returning visitor detection →</LinkTo>
          {{/if}}
        </p>
      {{/if}}
    </div>
  </template>
}
